import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import Fuse from 'fuse.js';
import { combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, take, takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/root/auth.service';
import { SnackBarService } from 'src/app/shared/snackbar.service';
import { fuse } from 'src/app/utilities/fuse';
import { CompanySwapperService } from '../company-swapper.service';

const searchOptions: Fuse.IFuseOptions<{ id: string; name: string }> = {
  distance: 100,
  keys: ['id', 'name'],
  location: 0,
  minMatchCharLength: 1,
  shouldSort: true,
  threshold: 0.2,
};

const MAX_COMPANIES_TO_SHOW = 50;

@Component({
  selector: 'lohi-company-switcher',
  templateUrl: './company-switcher.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanySwitcherComponent implements OnInit, OnDestroy {
  private destroy$$ = new Subject();
  public companySearch = new FormControl();
  public companyPicker = new FormControl();
  public companies$: Observable<{ id: string; name: string }[]>;

  constructor(
    private companySwapper: CompanySwapperService,
    private authService: AuthService,
    private snackBar: SnackBarService,
  ) {}
  public ngOnDestroy(): void {
    this.destroy$$.next();
    this.destroy$$.unsubscribe();
  }

  public ngOnInit(): void {
    this.doSetup();
  }

  private async doSetup() {
    const userInfo = await this.authService.userInfo$.pipe(take(1)).toPromise();
    this.companyPicker.setValue(userInfo.companyId);
    this.companies$ = combineLatest([
      this.companySwapper.companies$.pipe(map(fuse(searchOptions))),
      this.companySearch.valueChanges.pipe(startWith('')),
    ]).pipe(
      map(([fused, searchTerm]) => {
        if (!fused) {
          return [];
        }
        if (!searchTerm) {
          return fused.data;
        }
        return fused.fuse.search(searchTerm).map((r) => r.item);
      }),
      map((data) => {
        return data.slice(0, Math.min(data.length, MAX_COMPANIES_TO_SHOW));
      }),
    );

    this.companyPicker.valueChanges
      .pipe(
        takeUntil(this.destroy$$),
        distinctUntilChanged(),
        filter((companyId) => !!companyId),
      )
      .subscribe((companyId) => {
        this.companySwapper.swapCompany(companyId).subscribe(() => {
          this.snackBar.showMessage('Company updated');
          this.authService.loadUserInfo();
        });
      });
  }
}
